import React, { createContext, Reducer, useReducer, useState, useMemo } from 'react';
import BillingMeuConsumptionApi from '../../api/Billing/BillingMeuConsumptionApi';
import { ConsolidatedMeuConsumption } from '../../models/billing/meuConsumption/ConsolidatedMeuConsumption';
import { RequestStatus } from '../../shared/Requests';
import { getMonthTwoDigits } from '../../utils/date';
import {
  BillingMetrics,
  getMetricStatus,
  MetricStatus,
  METRIC_NOT_REGISTERED,
  UNLIMITED_PLAN_METRIC,
} from '../../shared/BillingMetrics';
import { equalsIgnoreOrder } from '../../utils/array';
import {
  localizationMeu as pageLocalizationMeu,
  localizationMsg as pageLocalizationMsg,
  localizationMeuMsg as pageLocalizationMeuMsg,
} from '../../views/Billing/MiuConsumption/localizations';
import {
  localizationMeu as dashLocalizationMeu,
  localizationMsg as dashLocalizationMsg,
  localizationMeuMsg as dashLocalizationMeuMsg,
} from '../../views/Billing/DataConsumption/components/MiuConsumption/localizations';
import FeatureToggleService from '../../services/FeatureToggleService';

interface CompiledMeuConsumptionDataState {
  consolidatedMeuConsumption: Array<ConsolidatedMeuConsumption>;
  consolidatedContainsDataOnSelectedMonth: boolean;
}

interface MeuConsumptionContextData {
  compiledMeuConsumptionDataState: CompiledMeuConsumptionDataState;
  consolidatedMeuApiRequisition: (
    dateToSearch: Date,
    metricIds: Array<BillingMetrics>,
    hasEnabledMetric: (value: Array<BillingMetrics>) => boolean,
  ) => Promise<void>;
  isMeuLoading: boolean;
  meuStatus: MetricStatus;
  meuMetricIds: Array<BillingMetrics>;
  localizationMeu: any;
}

interface Props {
  tenant: any;
}

export const MeuConsumptionContext = createContext<MeuConsumptionContextData>(
  {} as MeuConsumptionContextData
);

export const MeuConsumptionContextProvider: React.FC<Props> = ({ children, tenant }) => {
  const [isMeuLoading, setIsMeuLoading] = useState(false);
  const [meuStatus, setMeuStatus] = useState(MetricStatus.normal);
  const billingMeuConsumptionApi = new BillingMeuConsumptionApi();

  const defaultMetrics = [
    new ConsolidatedMeuConsumption(null, BillingMetrics.monthlyEngagedUser),
    new ConsolidatedMeuConsumption(null, BillingMetrics.activeMessage),
  ];

  const [compiledMeuConsumptionDataState, setCompiledMeuConsumptionDataState] =
    useReducer<
      Reducer<CompiledMeuConsumptionDataState, Partial<CompiledMeuConsumptionDataState>>
    >(
      (state, newState) => ({
        ...state,
        ...newState,
      }),
      {
        consolidatedMeuConsumption: defaultMetrics,
        consolidatedContainsDataOnSelectedMonth: false,
      }
    );

  const contentLocalization = [
    {
      metricId: [BillingMetrics.monthlyEngagedUser],
      localizationMetricId: [BillingMetrics.monthlyEngagedUser],
      localization: {
        pageLocalization: pageLocalizationMeu,
        dashLocalization: dashLocalizationMeu,
        asideMenuLocalization: 'meuConsumption',
      },
    },
    {
      metricId: [BillingMetrics.exchangedMessage],
      localizationMetricId: [BillingMetrics.exchangedMessage],
      localization: {
        pageLocalization: pageLocalizationMsg,
        dashLocalization: dashLocalizationMsg,
        asideMenuLocalization: 'msgConsumption',
      },
    },
    {
      metricId: [BillingMetrics.monthlyEngagedUser, BillingMetrics.activeMessage],
      localizationMetricId: [BillingMetrics.monthlyEngagedUser, BillingMetrics.activeMessage],
      localization: {
        pageLocalization: pageLocalizationMeuMsg,
        dashLocalization: dashLocalizationMeuMsg,
        asideMenuLocalization: 'meuMsgConsumption',
      },
    },
  ];

  const meuMetrics = (tenant?.subscription || []).filter(
    (item) =>
      item?.metricId === BillingMetrics.monthlyEngagedUser ||
      item?.metricId === BillingMetrics.activeMessage,
  );

  const metricIds = meuMetrics.map((item) => item.metricId);

  const meuMetricIds =
    contentLocalization.find((item) => equalsIgnoreOrder(item.metricId, metricIds))
      ?.localizationMetricId || [BillingMetrics.monthlyEngagedUser, BillingMetrics.activeMessage];

  const localizationMeu =
    contentLocalization.find((item) => equalsIgnoreOrder(item.metricId, meuMetricIds))
      ?.localization;

  const consolidatedMeuApiRequisition = async (
    dateToSearch: Date,
    metricIds: Array<BillingMetrics>,
    hasEnabledMetric: (value: Array<BillingMetrics>) => boolean
  ): Promise<void> => {
    try {
      setIsMeuLoading(true);

      const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
      const metricIdsString = metricIds.join(';');
      const storageType = await FeatureToggleService.isDatabricksStorageTypeEnabled(
        BillingMetrics.monthlyEngagedUser
      )
        ? 1
        : 0;

      const consolidated = hasEnabledMetric([
        BillingMetrics.monthlyEngagedUser,
        BillingMetrics.activeMessage,
      ])
        ? await billingMeuConsumptionApi.getConsolidatedMeuConsumption(
          tenant.info.id,
          metricIdsString,
          monthString,
          storageType
        )
        : [];

      const mergedConsolidated = defaultMetrics.map((defaultMetric) => {
        const apiMetric = consolidated.find(
          (item) => item.metric.metricId === defaultMetric.metric.metricId
        );
        return apiMetric ?? defaultMetric;
      });

      setCompiledMeuConsumptionDataState({
        consolidatedMeuConsumption: mergedConsolidated,
        consolidatedContainsDataOnSelectedMonth:
          billingMeuConsumptionApi.getStatus() === RequestStatus.SUCCESS,
      });

      if (billingMeuConsumptionApi.getStatus() === RequestStatus.SUCCESS) {
        setMeuStatus(getMetricStatus(UNLIMITED_PLAN_METRIC));
      } else {
        setMeuStatus(getMetricStatus(METRIC_NOT_REGISTERED));
      }
    } finally {
      setIsMeuLoading(false);
    }
  };

  const contextValue = useMemo(
    () => ({
      compiledMeuConsumptionDataState,
      consolidatedMeuApiRequisition,
      isMeuLoading,
      meuStatus,
      meuMetricIds,
      localizationMeu,
    }),
    [
      compiledMeuConsumptionDataState,
      consolidatedMeuApiRequisition,
      isMeuLoading,
      meuStatus,
      meuMetricIds,
      localizationMeu,
    ]
  );

  return (
    <MeuConsumptionContext.Provider value={contextValue}>
      {children}
    </MeuConsumptionContext.Provider>
  );
};

export function useMeuConsumptionContext(): MeuConsumptionContextData {
  const context = React.useContext(MeuConsumptionContext);
  if (!context) {
    throw new Error(
      'useMeuConsumptionContext must be used within a MeuConsumptionContextProvider'
    );
  }
  return context;
}